import { useState } from 'react'

import { useDebouncedCallback } from 'use-debounce'

import { type RepoPaginationResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginationResponse'

export const CATALOG_CONFIG_PAGE_SIZE = 10
const SEARCH_DEBOUNCE_MS = 300

export const DEFAULT_CATALOG_CONFIG_PAGING: RepoPaginationResponse = {
  count: 0,
  page: 1,
  pageSize: CATALOG_CONFIG_PAGE_SIZE,
  numPages: 1,
}

export interface CatalogConfigListModelParams {
  search: string
  page: number
  limit: number
  offset: number
}

/**
 * Shared search + pagination state for the catalog-permission config lists
 * (users tab / secondary-categories tab). All query params live in one state
 * object so updates are atomic (single re-render → single request), avoiding
 * the React 16 non-batching issue where sequential setState calls in a debounce
 * callback fire separate requests with an inconsistent param combination.
 */
export const useCatalogPermissionConfigListState = () => {
  const [modelParams, setModelParams] = useState<CatalogConfigListModelParams>({
    search: '',
    page: 1,
    limit: CATALOG_CONFIG_PAGE_SIZE,
    offset: 0,
  })
  const [searchInput, setSearchInput] = useState('')

  const updateDebouncedSearch = useDebouncedCallback((value: string) => {
    setModelParams((prev) => ({ ...prev, search: value, page: 1, offset: 0 }))
  }, SEARCH_DEBOUNCE_MS)

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    updateDebouncedSearch(value)
  }

  const handleSearchSubmit = (value: string) => {
    updateDebouncedSearch.cancel()
    setSearchInput(value)
    setModelParams((prev) => ({ ...prev, search: value, page: 1, offset: 0 }))
  }

  const handlePageChange = (page: number) => {
    setModelParams((prev) => ({
      ...prev,
      page,
      offset: (page - 1) * CATALOG_CONFIG_PAGE_SIZE,
    }))
  }

  return {
    modelParams,
    searchInput,
    handleSearchChange,
    handleSearchSubmit,
    handlePageChange,
  }
}
